import { validateImportReviewPayload } from './appPayloadReview';

const WRITE_UNSAFE_KEYS = new Set([
  'body',
  'charid',
  'characterid',
  'campaignid',
  'contenthtml',
  'description',
  'descriptions',
  'details',
  'effecttext',
  'flavortext',
  'fulltext',
  'html',
  'lines',
  'message',
  'messages',
  'ocr',
  'ownerid',
  'pages',
  'password',
  'privatetext',
  'providerdiagnostics',
  'providerstderr',
  'providerstdout',
  'raw',
  'rawfields',
  'rawjson',
  'rawocr',
  'rawocrtext',
  'rawregions',
  'rawsections',
  'rawsource',
  'rawtext',
  'reviewtext',
  'rulestext',
  'sourcefields',
  'sourcejson',
  'sourcetext',
  'sourceurl',
  'sourceurls',
  'text',
  'textpreview',
  'url',
  'urls',
  'userid',
  'words',
]);
const NOTE_KEYS = new Set(['note', 'notes']);

function assertFunction(value, message) {
  if (typeof value !== 'function') throw new Error(message);
}

function cloneJson(value) {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
}

function compactObject(value) {
  return Object.entries(value || {})
    .filter(([, child]) => child !== undefined && child !== null)
    .reduce((next, [key, child]) => {
      next[key] = child;
      return next;
    }, {});
}

function patchKeys(patch) {
  return Object.keys(patch || {}).filter((key) => key !== 'importReceipt').sort();
}

function sanitizeApplyCallbackResult(result) {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return {};
  return compactObject({
    characterId: typeof result.characterId === 'string' && result.characterId.trim()
      ? result.characterId.trim()
      : undefined,
  });
}

function mergeApplyResult(prepared, result) {
  return {
    ...sanitizeApplyCallbackResult(result),
    ...prepared,
  };
}

function assertNoWriteUnsafeKeys(value, path = 'payload') {
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((child, index) => assertNoWriteUnsafeKeys(child, `${path}[${index}]`));
    return;
  }
  for (const [key, child] of Object.entries(value)) {
    if (NOTE_KEYS.has(key.toLowerCase()) && hasNonEmptyText(child)) {
      throw new Error(`Import apply payload contains unsafe write field: ${path}.${key}`);
    }
    if (WRITE_UNSAFE_KEYS.has(key.toLowerCase())) {
      throw new Error(`Import apply payload contains unsafe write field: ${path}.${key}`);
    }
    assertNoWriteUnsafeKeys(child, `${path}.${key}`);
  }
}

function hasNonEmptyText(value) {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some((entry) => hasNonEmptyText(entry));
  if (value && typeof value === 'object') return Object.values(value).some((entry) => hasNonEmptyText(entry));
  return false;
}

export function assertImportWriteSafeValue(value, path = 'payload') {
  assertNoWriteUnsafeKeys(value, path);
  return value;
}

function validateWriteSafePayload(payload) {
  const safePayload = validateImportReviewPayload(payload);
  assertImportWriteSafeValue(safePayload, 'payload');
  return safePayload;
}

export function prepareReviewedImportCreate(payload) {
  const safePayload = validateWriteSafePayload(payload);
  if (safePayload.mode !== 'create') throw new Error('Expected a create import payload.');

  const createArgs = compactObject({
    name: typeof safePayload.createArgs?.name === 'string' && safePayload.createArgs.name.trim()
      ? safePayload.createArgs.name.trim()
      : 'Imported',
    race: typeof safePayload.createArgs?.race === 'string' && safePayload.createArgs.race.trim()
      ? safePayload.createArgs.race.trim()
      : 'Unknown',
    maxHp: Number.isFinite(safePayload.createArgs?.maxHp) ? safePayload.createArgs.maxHp : 1,
  });
  const patch = compactObject(cloneJson(safePayload.patch));

  return {
    mode: 'create',
    createArgs,
    patch,
    warnings: cloneJson(safePayload.warnings || {}),
    auditHint: {
      meta: {
        source: 'character-import',
        mode: 'create',
        patchKeys: patchKeys(patch),
      },
    },
  };
}

export function prepareReviewedImportUpdate(payload, { characterId } = {}) {
  const safePayload = validateWriteSafePayload(payload);
  if (safePayload.mode !== 'update') throw new Error('Expected an update import payload.');
  if (typeof characterId !== 'string' || !characterId) {
    throw new Error('Update import requires a target character.');
  }
  const patch = compactObject(cloneJson(safePayload.patch));

  return {
    mode: 'update',
    characterId,
    patch,
    warnings: cloneJson(safePayload.warnings || {}),
    auditHint: {
      meta: {
        source: 'character-import',
        mode: 'update',
        patchKeys: patchKeys(patch),
      },
    },
  };
}

export async function applyReviewedImportPayload(payload, app = {}) {
  const mode = payload?.mode;
  if (mode === 'create') {
    assertFunction(app.onApplyCreate, 'Create import apply callback is unavailable.');
    const prepared = prepareReviewedImportCreate(payload);
    const result = await app.onApplyCreate(prepared);
    return mergeApplyResult(prepared, result);
  }
  if (mode === 'update') {
    assertFunction(app.onApplyUpdate, 'Update import apply callback is unavailable.');
    const prepared = prepareReviewedImportUpdate(payload, { characterId: app.characterId });
    const result = await app.onApplyUpdate(prepared);
    return mergeApplyResult(prepared, result);
  }
  throw new Error('Import payload mode must be create or update.');
}
